import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useState, useEffect, useMemo } from "react";
import productCore from "../../core/product.core";
import wishListCore from "../../core/wishList.core";
import ProductCardHeader from "../Header/ProductHeader";
import { ProductGrid } from "../common/ProductGrid.comp";
import { ProductListRes } from "../../types/product";
import profileCore from "../../core/profile.core";
import useProductsGiftCard from "../../hooks/useProductsGiftCard.hook";

interface ProductSection {
  id: string;
  title: string;
  slug: string;
  viewAllLink?: string;
  showArrow?: boolean;
  isGift: boolean;
  isCoupon: boolean;
}

const PRODUCT_SECTIONS: ProductSection[] = [
  // { id: "1", title: "預購 2025 Guru糭", slug: "預購-2025-Guru糭", isGift: false, isCoupon: false },
  {
    id: "1",
    title: "Guru乜都有 新貨速覽",
    slug: "Guru乜都有-新貨速覽",
    showArrow: true,
    isGift: false,
    isCoupon: false,
  },
  { id: "2", title: "護膚品", slug: "護膚品", isGift: false, isCoupon: false },
  // { id: "4", title: "Guru Dollar", slug: "gitfCard", isGift: true, isCoupon: false },
  // { id: "5", title: "Coupons", slug: "coupons", isGift: false, isCoupon: true },
];

export default function ProductCategorySection() {
  const { isReady, locale } = useRouter();
  const [wishlistedItems, setWishlistedItems] = useState<Set<string>>(new Set());

  // Fetch wishlist
  const { data: wishList } = useQuery({
    queryKey: ["WishList"],
    queryFn: () => wishListCore.getWishList({}),
    enabled: isReady,
  });

  // Sync wishlist state with backend
  useEffect(() => {
    if (wishList?.products) {
      const newWishlistedItems = new Set<string>();
      wishList.products.forEach((product) => {
        newWishlistedItems.add(product.id);
      });
      setWishlistedItems(newWishlistedItems);
    }
  }, [wishList]);

  async function handleWishlistUpdate(productId: string, status: boolean) {
    try {
      if (status) {
        await wishListCore.addProduct(productId);
      } else {
        await wishListCore.removeProduct(productId);
      }

      setWishlistedItems((prev) => {
        const newSet = new Set(prev);
        status ? newSet.add(productId) : newSet.delete(productId);
        return newSet;
      });
    } catch (error) {
      console.error("Error updating wishlist:", error);
    }
  }

  return (
    <div className="py-8">
      <div className="space-y-12">
        {PRODUCT_SECTIONS.map((section) => (
          <GeneralProductGrid
            key={section.id}
            section={section}
            wishlistedItems={wishlistedItems}
            handleWishlistUpdate={handleWishlistUpdate}
          />
        ))}
        <GuruDollarGrid />
        {/* <CouponGrid /> */}
      </div>
    </div>
  );
}

function GeneralProductGrid({
  section,
  wishlistedItems,
  handleWishlistUpdate,
}: {
  section: ProductSection;
  wishlistedItems: Set<string>;
  handleWishlistUpdate: (productId: string, status: boolean) => void;
}) {
  const { isReady, locale } = useRouter();

  const { data: productsResponse, isLoading } = useQuery<ProductListRes>({
    queryKey: ["products", locale, section.slug],
    queryFn: () => productCore.getProductsBySlug(section.slug, locale),
    enabled: isReady,
  });

  const products = productsResponse?.list.filter((p) => p.productType !== "giftCard") ?? [];

  return (
    <section>
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
        <ProductCardHeader
          text={section.title}
          viewAllLink={section.viewAllLink ?? (section.showArrow ? `/category/${section.slug}/1` : undefined)}
          showArrow={section.showArrow}
          isLink={!!section.viewAllLink || !!section.showArrow}
        />
        <div className="mt-6">
          <ProductGrid
            products={isLoading ? [] : products}
            isLoading={isLoading}
            wishlistedItems={wishlistedItems}
            updateItem={handleWishlistUpdate}
            onReedem={section.isCoupon ? true : false}
          />
        </div>
      </div>
    </section>
  );
}

function GuruDollarGrid() {
  const { data: giftCardsTypesIDs } = useQuery({
    queryKey: ["giftCardsTypesIDs"],
    queryFn: () => profileCore.getGiftCardsTypeIDs("guru-default"),
  });

  const { data: giftResponse, isPending } = useProductsGiftCard();

  const guruDollar = useMemo(() => {
    if (!giftResponse?.list || !giftCardsTypesIDs || !Array.isArray(giftCardsTypesIDs)) return;

    return giftResponse.list.filter((product) => {
      const defId = product.giftCardDefId?.toString();
      return defId && giftCardsTypesIDs.includes(defId);
    });
  }, [giftResponse, giftCardsTypesIDs]);

  return (
    <section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ProductCardHeader
          text="Guru Dollar"
          // viewAllLink={section.viewAllLink}
          showArrow={false}
          isLink={true}
        />
        <div className="mt-6">
          <ProductGrid
            products={guruDollar || []}
            isLoading={isPending}
            wishlistedItems={new Set<string>()}
            updateItem={() => {}}
            onReedem={false}
          />
        </div>
      </div>
    </section>
  );
}

// function CouponGrid() {
//   const {
//     data: giftCardsTypesIDs,
//     isError: typeError,
//     isPending: tyePending,
//   } = useQuery({
//     queryKey: ["giftCardsTypesIDs"],
//     queryFn: () => profileCore.getGiftCardsTypeIDs("guru-default"),
//   });

//   const { data: giftResponse, isPending } = useProductsGiftCard();

//   const coupons = useMemo(() => {
//     if (!giftResponse?.list || !giftCardsTypesIDs || !Array.isArray(giftCardsTypesIDs)) return;

//     const matchedIds = giftCardsTypesIDs.map(String);

//     return giftResponse.list.filter((product) => {
//       const defId = product.giftCardDefId?.toString();
//       return !defId || !matchedIds.includes(defId);
//     });
//   }, [giftResponse, giftCardsTypesIDs]);

//   return (
//     <section>
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         <ProductCardHeader
//           text={"Coupons"}
//           // viewAllLink={false}
//           showArrow={false}
//           isLink={true}
//         />
//         <div className="mt-6">
//           <ProductGrid
//             products={coupons || []}
//             isLoading={isPending}
//             wishlistedItems={new Set<string>()}
//             updateItem={() => {}}
//             onReedem={true}
//           />
//         </div>
//       </div>
//     </section>
//   );
// }

